import { useEffect, useState } from "react";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams";
import { captureMessage, captureException } from "@sentry/react";
import { track } from "../utils/rudderstack";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import { GOOGLE_MAP_STYLES } from "./map-styles.ts";
import { MapPin } from "lucide-react";
import { usePartnerConfig, mergePartnerConfig } from "../hooks/usePartnerConfig";
import { resolveUrlTemplate } from "../utils/urlTemplate";

// Re-export so existing test imports from this file still work
export { resolveUrlTemplate };

type RedirectState = {
  countdown: number;
  hasError: string | null;
  hasRedirected: boolean;
};

export function RedirectPageContent({ config }: { config: LandingPageConfig }) {
  const [{ formData, apiResponse, priorityStudyId }] =
    useQuestionnaireQueryStates();
  const partnerConfig = usePartnerConfig();
  const [state, setState] = useState<RedirectState>({
    countdown: 7, // In seconds
    hasError: null,
    hasRedirected: false,
  });

  const trialSiteDetails = apiResponse?.outcome?.trialSiteDetails;
  const patientId = apiResponse?.outcome?.patientId;

  // Resolve template variables if external redirect is configured
  const externalRedirectUrl = config.externalRedirectUrl
    ? resolveUrlTemplate(config.externalRedirectUrl, {
        patientId: patientId || '',
        priorityStudyId: priorityStudyId || '',
        landingPageId: config.id,
      })
    : null;

  const redirectUrl = externalRedirectUrl || (priorityStudyId
    ? `https://app.healthmatch.io/trials/${priorityStudyId}?openBookingModal=true`
    : "https://app.healthmatch.io");

  useEffect(() => {
    if (!formData || !apiResponse) {
      captureMessage(
        "Redirect page loaded without form data or API response",
        {
          level: "error",
          tags: {
            landing_page_id: config.id,
            error_type: "missing_form_data",
          },
        },
      );
      window.location.href = `/${config.id}`;
      return;
    }

    // Track redirect page view
    track("lp.redirect_page.viewed", {
      landing_page_id: config.id,
      has_trial_site_details: true, // should always have a site at this point
      has_priority_study_id: !!priorityStudyId,
      is_external_redirect: !!externalRedirectUrl,
      external_redirect_url: externalRedirectUrl || undefined,
    });

    // Only show error if we don't have external redirect configured and no priorityStudyId
    if (!externalRedirectUrl && !priorityStudyId) {
      setState((prev) => ({
        ...prev,
        hasError: "Missing data required for redirection",
      }));

      captureException(
        new Error("Missing trial site details or study ID for redirection"),
        {
          tags: {
            landing_page_id: config.id,
            error_type: "missing_redirect_data",
            missing_trial_details: false,
            missing_study_id: !priorityStudyId,
            has_external_redirect: false,
          },
        },
      );
      return;
    }
  }, [config.id, formData, apiResponse, priorityStudyId, externalRedirectUrl]);

  // Handle countdown and redirect
  useEffect(() => {
    const { hasRedirected } = state;

    // Don't redirect if there's an error or already redirected
    if (hasRedirected) {
      return;
    }

    const countdownInterval = setInterval(() => {
      setState((prev) => {
        if (prev.countdown <= 1 && !prev.hasRedirected) {
          clearInterval(countdownInterval);

          try {
            // Track redirect event before the actual redirect happens
            track("lp.redirect_page.redirected", {
              landing_page_id: config.id,
              destination: redirectUrl,
              study_id: priorityStudyId,
              has_trial_site_details: true,
              is_external_redirect: !!externalRedirectUrl,
              external_redirect_url: externalRedirectUrl || undefined,
            });

            window.location.href = redirectUrl;
            return { ...prev, countdown: 0, hasRedirected: true };
          } catch (error) {
            const errorMsg =
              error instanceof Error
                ? error.message
                : "Unknown error during redirect";
            captureException(error, {
              tags: {
                error_type: "redirect_failed",
                landing_page_id: config.id,
              },
            });
            return { ...prev, countdown: 0, hasError: errorMsg };
          }
        }

        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [state, config.id, redirectUrl, priorityStudyId, externalRedirectUrl]);

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="redirect-page">
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-8 pb-4 md:pb-20"
          data-testid="redirect-content"
        >
          <div className="text-center">
            <div
              className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-bold text-gray-900 sm:text-3xl md:text-4xl px-2 sm:px-4"
              data-testid="redirect-title"
            >
              <span className="block mb-3 md:mb-4">
                Application Successful!
              </span>
              <span className="block text-xl md:text-2xl bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] md:leading-[1.3] overflow-visible">
                Taking you to your clinical trial
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <div className="w-full max-w-4xl mx-auto px-2">
              <div
                className="bg-white rounded-2xl overflow-hidden p-8 flex flex-col items-center justify-center text-center"
                data-testid="redirect-card"
              >
                {state.hasError ? (
                  <div data-testid="redirect-error">
                    <div className="mb-6 text-red-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        className="h-16 w-16 mx-auto"
                      >
                        <circle cx="12" cy="12" r="10" strokeWidth="2"></circle>
                        <line
                          x1="12"
                          y1="8"
                          x2="12"
                          y2="12"
                          strokeWidth="2"
                        ></line>
                        <line
                          x1="12"
                          y1="16"
                          x2="12.01"
                          y2="16"
                          strokeWidth="2"
                        ></line>
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      We encountered an issue
                    </h3>
                    <p className="text-gray-600 mb-6">
                      We're redirecting you to find suitable trials.
                    </p>
                  </div>
                ) : (
                  <>
                    <div
                      className="mb-6"
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {trialSiteDetails?.latitude != null &&
                      trialSiteDetails?.longitude != null &&
                      trialSiteDetails?.name != null &&
                      trialSiteDetails?.address != null ? (
                        <div className="w-full h-[350px] text-center shadow-xl">
                          <div className="h-[250px]">
                            <APIProvider apiKey="AIzaSyBTO3fCm_GIGsMV4TltJPAngo3kpCVKQg0">
                              <Map
                                data-testid="trial-site-map"
                                defaultCenter={{
                                  lat: trialSiteDetails.latitude,
                                  lng: trialSiteDetails.longitude,
                                }}
                                defaultZoom={12}
                                disableDefaultUI={false}
                                zoomControl={false}
                                mapTypeControl={false}
                                scaleControl={false}
                                streetViewControl={false}
                                rotateControl={false}
                                fullscreenControl={false}
                                keyboardShortcuts={false}
                                gestureHandling={"none"}
                                scrollwheel={false}
                                disableDoubleClickZoom={true}
                                cameraControl={false}
                                styles={GOOGLE_MAP_STYLES}
                              >
                                <Marker
                                  position={{
                                    lat: trialSiteDetails.latitude,
                                    lng: trialSiteDetails.longitude,
                                  }}
                                />
                              </Map>
                            </APIProvider>
                          </div>
                          <div className="w-full h-[100px] flex flex-col justify-center items-center overflow-hidden">
                            <div className="bold text-2xl w-full truncate px-4 pl-8 text-left">
                              <span className="flex items-center">
                                <MapPin className="mr-1 flex-shrink-0" />
                                <span className="truncate">
                                  {trialSiteDetails.name}
                                </span>
                              </span>
                            </div>
                            <div className="text-gray-400 truncate w-full px-4 pl-12 text-left">
                              {trialSiteDetails.address}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Loader />
                      )}
                    </div>

                    <p className="text-lg text-gray-700 mb-4">
                      We're directing you to the trial site to complete your
                      application
                    </p>

                    <p
                      className="text-sm text-gray-500"
                      data-testid="redirect-countdown"
                    >
                      Redirecting in {state.countdown}{" "}
                      {state.countdown === 1 ? "second" : "seconds"}...
                    </p>

                    <div className="mt-8">
                      <p className="text-sm text-gray-600 mb-2">
                        Not being redirected?
                      </p>
                      {(externalRedirectUrl || priorityStudyId) ? (
                        <a
                          href={redirectUrl}
                          className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                          onClick={() => {
                            track("lp.redirect_page.manual_redirect_clicked", {
                              landing_page_id: config.id,
                              destination: redirectUrl,
                              study_id: priorityStudyId,
                              is_external_redirect: !!externalRedirectUrl,
                              external_redirect_url: externalRedirectUrl || undefined,
                            });
                          }}
                          data-testid="redirect-manual-link"
                        >
                          Continue to clinical trial
                        </a>
                      ) : (
                        <a
                          href={`/${config.id}/no-sites`}
                          className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                          data-testid="redirect-find-other"
                        >
                          Find other trials
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}

const Loader = () => (
  <div
    className="animate-spin rounded-full h-20 w-20 border-b-4 border-primary-600 mb-12 mt-12"
    data-testid="redirect-spinner"
  ></div>
);
